import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';

const NotificationsDrawer = ({
  isOpen,
  onClose,
  items,
  unreadCount,
  loading,
  error,
  hasMore,
  load,
  markRead,
  markAllRead,
  reload
}) => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const drawerRef = useRef(null);

  // Load notifications when drawer opens
  useEffect(() => {
    if (isOpen) {
      load({ limit: 20, offset: 0 });
    }
  }, [isOpen, load]);

  // Handle ESC key to close drawer
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when drawer is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Format relative time
  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h ago`;
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Handle notification item click
  const handleNotificationClick = async (notification) => {
    // Mark as read if unread
    if (!notification.read) {
      await markRead(notification.id);
    }
    
    // Keep drawer open as per requirements
  };

  // Handle mark all as read
  const handleMarkAllRead = async () => {
    if (unreadCount > 0) {
      await markAllRead();
    }
  };

  // Handle see all notifications - navigate to client portal notifications page
  const handleSeeAll = () => {
    navigate('/app/notifications');
    onClose();
  };

  // Load more notifications
  const handleLoadMore = () => {
    if (hasMore && !loading) {
      load({ limit: 20, offset: items.length });
    }
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[150] transition-opacity duration-200" />
      )}
      
      {/* Drawer */}
      <div
        ref={drawerRef}
        className={`fixed top-0 right-0 h-full w-full max-w-md md:w-96 z-[160] transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        } ${
          isDark 
            ? 'bg-gray-800 border-l border-gray-700' 
            : 'bg-white border-l border-gray-200'
        }`}
        style={{ 
          top: '96px', // Below the header
          height: 'calc(100vh - 96px)'
        }}
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <h2 className={`text-lg font-semibold ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            Notifications
          </h2>
          
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors duration-200 ${
                  isDark
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                Mark all read
              </button>
            )}
            
            <button
              onClick={onClose}
              className={`p-1 rounded-md hover:bg-opacity-50 transition-colors duration-200 ${
                isDark 
                  ? 'hover:bg-gray-700' 
                  : 'hover:bg-gray-100'
              }`}
              aria-label="Close notifications"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col h-full">
          {/* Footer at Top */}
          <div className={`flex-shrink-0 p-4 border-b ${
            isDark ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'
          }`}>
            <button
              onClick={handleSeeAll}
              className={`w-full py-3 text-sm font-medium rounded-lg transition-colors duration-200 text-white`}
              style={{
                backgroundColor: '#7d6633'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#6b5629'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#7d6633'}
            >
              See all notifications
            </button>
          </div>

          {/* Notifications List */}
          <div className="flex-1 overflow-y-auto">
            {loading && items.length === 0 ? (
              <div className="flex items-center justify-center p-8">
                <div className={`animate-spin rounded-full h-8 w-8 border-2 border-transparent border-t-current ${
                  isDark ? 'text-blue-400' : 'text-blue-500'
                }`}></div>
              </div>
            ) : error ? (
              <div className={`p-8 text-center ${
                isDark ? 'text-gray-400' : 'text-gray-500'
              }`}>
                <p className="mb-4">Failed to load notifications</p>
                <button
                  onClick={reload}
                  className={`px-4 py-2 rounded-md font-medium transition-colors duration-200 ${
                    isDark
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  Try again
                </button>
              </div>
            ) : items.length === 0 ? (
              <div className={`p-8 text-center ${
                isDark ? 'text-gray-400' : 'text-gray-500'
              }`}>
                <svg className="h-12 w-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                <p className="text-lg font-medium mb-2">No notifications yet</p>
                <p className="text-sm">You'll see updates about your bookings here.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {items.map((notification) => (
                  <button
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`w-full text-left p-4 hover:bg-opacity-50 transition-colors duration-200 ${
                      isDark 
                        ? 'hover:bg-gray-700' 
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Status dot */}
                      {!notification.read && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium mb-1 ${
                          !notification.read 
                            ? (isDark ? 'text-white' : 'text-gray-900')
                            : (isDark ? 'text-gray-300' : 'text-gray-600')
                        }`}>
                          {notification.message}
                        </p>
                        
                        <div className="flex items-center gap-2 text-xs">
                          <span className={`${
                            isDark ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            {formatRelativeTime(notification.created_at)}
                          </span>
                          
                          {notification.type && (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              notification.type.startsWith('booking') ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                              notification.type.startsWith('client') ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                              notification.type.startsWith('service') ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                              notification.type.startsWith('barber') ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                              'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                            }`}>
                              {notification.type.replace('.', ' ')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
                
                {/* Load more button */}
                {hasMore && (
                  <div className="p-4 text-center border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={handleLoadMore}
                      disabled={loading}
                      className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                        loading
                          ? (isDark ? 'text-gray-400' : 'text-gray-500')
                          : isDark
                            ? 'bg-blue-600 hover:bg-blue-700 text-white'
                            : 'bg-blue-600 hover:bg-blue-700 text-white'
                      }`}
                    >
                      {loading ? 'Loading...' : 'Load more'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

        </div>
      </div>
    </>
  );
};

export default NotificationsDrawer;
